/** Every write the app makes. Collected here rather than scattered through
 *  screens so the shape of what touches the database is readable in one file.
 *
 *  None of these pass user_id: the column defaults to auth.uid() and the RLS
 *  policy checks it, so the database decides ownership rather than the client
 *  asserting it. */
import { supabase } from './supabase'
import { berlinDate } from './ics'
import type { ListKind, ListItem, StayKind, Lead } from './types'
import { restoreRow, type StoredListItem } from './listRestore'
import type { ProfilePatch } from './profileDefaults'
import type { Json } from './database.types'
import type { VectorJson } from './tasteForm'

export const addCommitment = (on_date: string, title: string, entry_id?: string) =>
  supabase.from('commitments').insert({ on_date, title, entry_id: entry_id ?? null })

export const removeCommitment = (id: string) =>
  supabase.from('commitments').delete().eq('id', id)

export const updateCommitment = (id: string, patch: { on_date: string; title: string }) =>
  supabase.from('commitments').update(patch).eq('id', id)

export const addStay = (s: { kind: StayKind; starts_on: string; ends_on: string; label: string | null; provisional: boolean; country: string | null }) =>
  supabase.from('stays').insert(s)

export const updateStay = (id: string, patch: { kind: StayKind; starts_on: string; ends_on: string; label: string | null; provisional: boolean; country: string | null }) =>
  supabase.from('stays').update(patch).eq('id', id)

export const addVisitor = (v: { who: string; starts_on: string; ends_on: string; at_mine: boolean; note?: string | null }) =>
  supabase.from('visitors').insert(v)

export const updateVisitor = (id: string, patch: { who: string; starts_on: string; ends_on: string; at_mine: boolean }) =>
  supabase.from('visitors').update(patch).eq('id', id)

export const removeVisitor = (id: string) =>
  supabase.from('visitors').delete().eq('id', id)

// ---------------------------------------------------------------------------
// Notes: the queue of things to fix in the app
// ---------------------------------------------------------------------------

/** Write a new order for a set of lists, one row at a time.
 *
 *  Sequential rather than an upsert: `lists` has a NOT NULL `name` and `kind`,
 *  so an upsert would have to send the whole row back to change one integer,
 *  and sending a stale name is how a rename gets undone by a reorder. Four
 *  lists is four requests and nobody notices.
 */
export const reorderLists = async (ids: string[]) => {
  let last: { error: { message: string } | null } = { error: null }
  for (const [i, id] of ids.entries()) {
    last = await supabase.from('lists').update({ position: i }).eq('id', id)
    if (last.error) return last
  }
  return last
}

export type NoteKind = 'fix' | 'change' | 'idea'

/** `venueId` and `entryId` are both optional and stay that way — the account
 *  sheet writes a note about the app in general, the venue admin screens write
 *  one about the venue on screen, and "Something wrong with this?" writes one
 *  about a single listing. Same table, same queue, same triage query; each
 *  column is null exactly when the note is not about that thing.
 *
 *  `entryId` is a column rather than a line in the body because it is counted:
 *  `useWrongReports` groups this week's notes by the entry's `source_recipe`
 *  for the health strip, and a body cannot be joined. The body still names it
 *  too — what the next session reads is the sentence. */
export const addNote = async (body: string, kind: NoteKind, venueId?: string, entryId?: string, createdAt?: string) =>
  await supabase.from('app_notes')
    .insert({
      body: body.trim(), kind, venue_id: venueId ?? null, entry_id: entryId ?? null,
      // Only Undo passes one (a2-35): the note goes back in its own place.
      ...(createdAt ? { created_at: createdAt } : {}),
    })

/** Reopening clears the resolution as well as the date. A sentence saying
 *  what was changed is false the moment the note is open again, and leaving
 *  it behind is the "one flag doing two jobs" shape that keeps costing time
 *  here. */
export const setNoteDone = async (id: string, done: boolean, resolution?: string) =>
  await supabase.from('app_notes')
    .update(done
      ? { done_at: new Date().toISOString(), resolution: resolution?.trim() || null }
      : { done_at: null, resolution: null })
    .eq('id', id)

export const editNote = async (id: string, body: string) =>
  await supabase.from('app_notes').update({ body: body.trim() }).eq('id', id)

export const removeNote = async (id: string) =>
  await supabase.from('app_notes').delete().eq('id', id)

export const removeFlight = async (id: string) =>
  await supabase.from('flights').delete().eq('id', id)

export const removeStay = (id: string) =>
  supabase.from('stays').delete().eq('id', id)

export const addFlight = (f: { on_date: string; depart_at: string | null; from_iata: string | null; to_iata: string | null; code: string }) =>
  supabase.from('flights').insert({ ...f, source: 'manual' })

export const updateFlight = (id: string, patch: { on_date: string; depart_at: string | null; from_iata: string | null; to_iata: string | null; code: string }) =>
  supabase.from('flights').update(patch).eq('id', id)

/** `flagLabel` is collection-only (`lists_flag_label_kind`) — a caller
 *  passing one for any other kind gets the CHECK constraint's own rejection
 *  rather than a silently dropped flag. */
export const addList = (name: string, kind: ListKind, flagLabel: string | null = null) =>
  supabase.from('lists').insert({ name, kind, flag_label: flagLabel })

/** `.select().single()` so a caller that needs the new row back — WP 4.5's
 *  paste path fills price, image and a real title into the row once
 *  `/link-preview` answers, and needs this insert's own id to do it — can
 *  read `.data`. Every existing caller already ignored the return value, so
 *  asking PostgREST to return a representation changes nothing for them. */
export const addItem = (
  list_id: string, body: string,
  opts: {
    section?: string | null; parent_id?: string | null; position?: number
    qty?: string | null; due_on?: string | null
  } = {},
) =>
  supabase.from('list_items').insert({
    list_id, body,
    section: opts.section ?? null,
    parent_id: opts.parent_id ?? null,
    position: opts.position ?? 0,
    // WP 4.4: `qty` is grocery-only and `due_on` task-only
    // (`list_item_kind_guard`) — a caller on any other kind never passes one.
    qty: opts.qty ?? null,
    due_on: opts.due_on ?? null,
  }).select().single()

export const setItemDone = (id: string, done: boolean) =>
  supabase.from('list_items').update({ done_at: done ? new Date().toISOString() : null }).eq('id', id)

/** Edit an item's own fields. `buy` items carry a link; groceries carry a
 *  qty; every kind carries a body. Only the keys given are written, so
 *  opening the sheet and saving does not blank a field it did not touch. */
export const updateItem = (id: string, patch: { body?: string; link?: string | null; qty?: string | null }) =>
  supabase.from('list_items').update(patch).eq('id', id)

/** Write a new order for a set of items: each id's position becomes its index.
 *  One statement (`reorder_list_items`, a3-29), not a PATCH per line — a
 *  fifteen-line list sent fifteen requests, and one failing part-way left
 *  the order half written. An upsert was never the answer: `list_items` has
 *  a NOT NULL body, so it would round-trip the whole row to move one integer.
 *  The desk's drag, its Alt+arrow and the phone's `TaskList` all come here. */
export const reorderItems = (ids: string[]) =>
  supabase.rpc('reorder_list_items', { ids })

export const removeItem = (id: string) =>
  supabase.from('list_items').delete().eq('id', id)

/** A template is reset rather than emptied: the run is logged, then every tick
 *  is cleared and the items survive for the next guest. */
export async function resetList(list_id: string, item_count: number) {
  await supabase.from('list_runs').insert({ list_id, item_count })
  return supabase.from('list_items').update({ done_at: null }).eq('list_id', list_id).not('done_at', 'is', null)
}

// ---------------------------------------------------------------------------
// WP 4.1: the columns L2–L9 need, on top of the shape above
// ---------------------------------------------------------------------------

export const renameList = (id: string, name: string) =>
  supabase.from('lists').update({ name }).eq('id', id)

export const pinList = (id: string, pinned = true) =>
  supabase.from('lists').update({ pinned }).eq('id', id)

/** `archived = false` is the undo: the row is never deleted by this. */
export const archiveList = (id: string, archived = true) =>
  supabase.from('lists').update({ archived_at: archived ? new Date().toISOString() : null }).eq('id', id)

/** Cascades to the list's items, runs and usuals — all three reference
 *  `lists (id) on delete cascade`. */
export const deleteList = (id: string) =>
  supabase.from('lists').delete().eq('id', id)

/** L1's "Change kind" menu action. The kind-specific columns on the list's
 *  own items are not cleared — `list_item_kind_guard` only fires on a
 *  `list_items` write, not on this one — so an item carrying a field from
 *  the old kind keeps it until something next writes that item. */
export const changeListKind = (id: string, kind: ListKind) =>
  supabase.from('lists').update({ kind }).eq('id', id)

export type ListItemFields = Partial<
  Pick<ListItem, 'qty' | 'due_on' | 'price_cents' | 'currency' | 'status' | 'image_url' | 'flagged_at' | 'note' | 'tags' | 'entry_id'>
>

/** The columns `updateItem` doesn't cover — one kind's own fields, per
 *  `list_item_kind_guard` in the schema. Only the keys given are written. */
export const setItemFields = (id: string, patch: ListItemFields) =>
  supabase.from('list_items').update(patch).eq('id', id)

/** L8: add a saved event to any list, from its sheet. */
export const addItemForEntry = (list_id: string, entry_id: string, body: string) =>
  supabase.from('list_items').insert({ list_id, entry_id, body })

/** L3: bump a grocery item's usual-count via the `bump_usual` RPC — a genuine
 *  increment, not an upsert that would overwrite `times` with whatever this
 *  call sent. */
export const bumpUsual = (list_id: string, body: string) =>
  supabase.rpc('bump_usual', { p_list_id: list_id, p_body: body })

export const listUsuals = (list_id: string) =>
  supabase.from('list_usuals').select('*').eq('list_id', list_id).order('times', { ascending: false })

/** L5: start a fresh run of a checklist — logs the run, then clears every
 *  tick, the same shape `resetList` uses for a template. Unlike `resetList`,
 *  the caller doesn't know `item_count` yet at the start of a run, so it's
 *  left null; `newRun` is the checklist's own entry point, `resetList` stays
 *  the template's. */
export async function newRun(list_id: string) {
  const res = await supabase.from('list_runs').insert({ list_id })
  if (res.error) return res
  return supabase.from('list_items').update({ done_at: null }).eq('list_id', list_id).not('done_at', 'is', null)
}

export const listRuns = (list_id: string) =>
  supabase.from('list_runs').select('*').eq('list_id', list_id).order('completed_at', { ascending: false })

// ---------------------------------------------------------------------------
// What you did with an entry
//
// Two writes, deliberately not one. `entry_state` says what is true now and is
// what the feed reads; `taste_signal` is the append-only log the learning loop
// reads. The state write is the one that must succeed.
//
// The signal write is allowed to fail. 003 revokes every grant on taste_signal
// from `authenticated`, and 004 adds a read policy without restoring the grant,
// so from the app it is currently `permission denied` — a policy with no grant
// behind it. Migration 005 fixes that. Until it is applied, a save must still
// save, so the log is best-effort and the failure is returned rather than
// thrown: the caller decides whether it is worth surfacing.
// ---------------------------------------------------------------------------

/** Features are denormalised into the signal on purpose: an entry can be
 *  re-extracted or deleted, and the signal has to keep meaning something. */
type SignalFeatures = {
  type?: string
  discipline?: string | null
  organisation?: string | null
  english_access?: string
  tags?: string[]
  /** WP 8.4: a rating's own action (`'rated'`) carries the star count
   *  alongside the row's other features, rather than a second insert. */
  stars?: number
  /** E4: the ids of the people and ensembles on the row, spread in by
   *  `signalFeatures` (`affinity.ts`'s `idsOf`) — `taste_affinity` sums
   *  them into a per-entity lift. Absent on a signal logged before E4;
   *  `[]` on a row that named no one. */
  person_ids?: string[]
  ensemble_ids?: string[]
}

/** Exported for `seen.ts`'s `markSeen`, the one other write that logs a
 *  taste signal — every other caller is in this file, so this stays the only
 *  export rather than a second copy of the insert. */
export async function logSignal(entry_id: string, action: string, features: SignalFeatures) {
  const { error } = await supabase.from('taste_signal').insert({ entry_id, action, features })
  return error
}

export async function saveEntry(entry_id: string, features: SignalFeatures) {
  const res = await supabase
    .from('entry_state')
    .upsert({ entry_id, state: 'saved', at: new Date().toISOString() }, { onConflict: 'user_id,entry_id' })
  if (res.error) return res
  await logSignal(entry_id, 'saved', features)
  return res
}

/**
 * The same write over every entry a card stands for, stopping at the first
 * error.
 *
 * A merged film card is three entries — a cinema listing is pulled weekly and
 * publishes only its own week, so one film playing three weeks is three rows.
 * Saving one of them and being shown the other two next time is not a thing
 * anyone wants, so the action applies to all of them.
 *
 * Returns one result rather than an array, because `useAction` surfaces one
 * error and an array of results is not one. The first failure wins and the
 * rest are not attempted: a partial save is already wrong, and continuing
 * would make it wrong in more places.
 */
export async function forEachEntry(
  ids: string[],
  write: (id: string) => Promise<{ error: { message: string } | null }>,
) {
  let last: { error: { message: string } | null } = { error: null }
  for (const id of ids) {
    last = await write(id)
    if (last.error) return last
  }
  return last
}

export async function dismissEntry(entry_id: string, features: SignalFeatures) {
  const res = await supabase
    .from('entry_state')
    .upsert({ entry_id, state: 'dismissed', at: new Date().toISOString() }, { onConflict: 'user_id,entry_id' })
  if (res.error) return res
  await logSignal(entry_id, 'dismissed', features)
  return res
}

/**
 * This listing is wrong — the date, the place, the language, the thing itself.
 * Take it off the screen, and write nothing to `taste_signal`.
 *
 * It used to write one: `action = 'skipped'`, with `features.recipe`, beside
 * the dismissal. The reasoning was that `taste_signal` is the record of every
 * judgement Doug makes and the action name tells the two apart. That is one
 * column doing two jobs, and the job it did badly was the important one — the
 * scorer reads the table, so a wrong *date* on a Philharmonie listing taught it
 * that Doug dislikes the Philharmonie. A statement about an adapter and a
 * statement about taste are different facts and do not belong in one table
 * however carefully they are labelled (WP 2.4, rule 4).
 *
 * The report itself is an `app_notes` row now, written by `EventActions` with
 * the entry id in its own column — a sentence a person can act on, in the queue
 * built for exactly that, joinable back to the recipe that produced the row.
 * `useWrongReports` counts them for the health strip.
 *
 * The dismissal stays: a wrong listing should also go away. So this function is
 * `dismissEntry` without the signal, and it keeps its own name because what it
 * means is different — the row is gone because it is broken, not because it was
 * not wanted.
 */
export async function markWrong(entry_id: string) {
  return await supabase
    .from('entry_state')
    .upsert({ entry_id, state: 'dismissed', at: new Date().toISOString() }, { onConflict: 'user_id,entry_id' })
}

/** Unsaving is a delete rather than a third state: "I never decided" and "I
 *  decided no" are different, and only the second belongs in entry_state. */
/** Undo a save or a dismissal. Awaited rather than returned raw, so it has the
 *  `{ error }` shape `useAction` and `forEachEntry` expect. */
export const forgetEntry = async (entry_id: string) =>
  await supabase.from('entry_state').delete().eq('entry_id', entry_id)

export const restoreDismissed = () =>
  supabase.from('entry_state').delete().eq('state', 'dismissed')

/**
 * WP 4.3: an explicit answer to "did you go", for a saved event whose date
 * has passed. Only from a tick — never from time passing (rule 1) — so this
 * has no caller that isn't a direct response to one.
 *
 * The brief this shipped from asked for two new `taste_signal` actions,
 * `went` and `skipped_after_save`; `taste_signal_action_check` allows neither.
 * `attended` and `skipped` already exist, already mean this, and the scorer
 * already reads them — two names for one meaning is the bug this codebase
 * keeps writing, so `went` decides which of the two existing actions this
 * insert is rather than becoming a third.
 */
export async function markWent(entry_id: string, went: boolean, features: SignalFeatures) {
  return { error: await logSignal(entry_id, went ? 'attended' : 'skipped', features) }
}

/**
 * Putting a saved event in the calendar. The commitment carries entry_id, so
 * the day on the timeline can open the event it came from.
 *
 * WP 3.3: `starts_at` is the showing's own exact moment — a chip's pick, or
 * `berlinMidnightIso` for a run with no clocked showing — and `on_date` is
 * derived from it rather than taken as a second argument, so the two columns
 * can never disagree about which day this is.
 */
export async function scheduleEntry(entry_id: string, starts_at: string, title: string, features: SignalFeatures) {
  const res = await supabase
    .from('commitments')
    .insert({ on_date: berlinDate(starts_at), starts_at, title, entry_id })
    .select('id')
    .single()
  if (res.error) return res
  await logSignal(entry_id, 'scheduled', features)
  return res
}

// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------

export const dismissLead = (id: string) =>
  supabase.from('leads').update({ status: 'dismissed' }).eq('id', id)

/**
 * Turn a lead into an entry Doug can save, and close the lead.
 *
 * `source_recipe = 'lead'` is not decoration: migration 019 lets the app write
 * `entries` only for rows that declare themselves promoted leads, so it is the
 * predicate the RLS policy checks. Everything the adapters own stays
 * untouchable from the client.
 *
 * The fingerprint is derived from the lead's own id rather than from the
 * title. The newsletter era keyed identity on a hash of model prose, which is
 * what produced two Fotografiskas; a lead has a uuid and it is already unique.
 */
export async function promoteLead(lead: Lead, type: string, venueId: string | null) {
  const x = lead.extracted
  if (!x) return { error: new Error('this lead has nothing structured to promote') }

  const dates = [...(x.dates ?? [])].sort()
  const { data, error } = await supabase
    .from('entries')
    .insert({
      fingerprint: `lead|${lead.id}`,
      source_recipe: 'lead',
      type,
      title: x.title,
      summary_en: x.summary,
      lang: 'de',
      english_access: 'unknown',
      url: x.url,
      venue_id: venueId,
      run_start: dates[0] ?? null,
      run_end: dates[dates.length - 1] ?? null,
      city: 'Berlin',
      status: 'announced',
      // Phase 8a: the same shape the nightly run's auto-promotion writes, so
      // a card promoted by hand and one promoted overnight are the same card.
      tags: ['newsletter'],
      newsletter: {
        organiser: x.organiser_guess,
        place_src: x.place_guess,
        time_src: x.time,
        text_src: lead.text?.slice(0, 2000) ?? null,
        url: x.url,
        mail_source: lead.mail_source,
        email_id: lead.email_id,
        lead_id: lead.id,
      },
    })
    .select('id')
    .single()

  if (error) return { error }

  // Every stated date becomes a showing, at the stated time where there is
  // one and at midnight where there is not — the app already suppresses
  // "00:00" on a card.
  const time = x.time ?? '00:00'
  const rows = dates.map(d => ({ entry_id: data.id, starts_at: `${d}T${time}:00+02:00`, venue_id: venueId }))
  if (rows.length) {
    const showings = await supabase.from('showings').insert(rows)
    if (showings.error) return { error: showings.error }
  }

  return supabase
    .from('leads')
    .update({ status: 'promoted', matched_entry_id: data.id })
    .eq('id', lead.id)
}

// ---------------------------------------------------------------------------
// WP 3.4b (P3): following a place
// ---------------------------------------------------------------------------

/** `user_id` defaults to `auth.uid()` (migration 20260926100000), same as
 *  every other personal table here. */
export const followVenue = (venueId: string) =>
  supabase.from('venue_follows').insert({ venue_id: venueId })

export const unfollowVenue = (venueId: string) =>
  supabase.from('venue_follows').delete().eq('venue_id', venueId)

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

/** Tapping an alert acknowledges it. There is no un-acknowledge, and there
 *  should not be: the alerter's own rule is that it says nothing while things
 *  are healthy or healing, so an alert that is still true will be raised
 *  again. */
export const acknowledgeAlert = (id: string) =>
  supabase.from('alerts').update({ acknowledged_at: new Date().toISOString() }).eq('id', id)

/**
 * Undo for a deleted list item (a2-14): the row back whole, under its own id
 * and `created_at`, so it lands where it was — `restoreRow` says which
 * columns. Waits for the delete (`gone`, its mutation's own promise, errors
 * swallowed) so the two cannot cross: an insert of the same id arriving
 * before the delete would be removed by it. An upsert, not an insert, so a
 * delete that failed and left the row in place makes this a no-op rather
 * than a duplicate-key error.
 */
export async function restoreItem(i: StoredListItem, gone?: Promise<unknown>) {
  await gone
  return supabase.from('list_items').upsert(restoreRow(i), { onConflict: 'id' })
}

// ---------------------------------------------------------------------------
// U1.2: the profile
// ---------------------------------------------------------------------------

/** The signed-in person's own row. No insert: a trigger on `auth.users`
 *  creates it at sign-up (U1.1). `uid` is passed — unlike the personal tables
 *  above, whose `user_id` defaults — because the update filters on it, and
 *  RLS (`profiles_own`) is what makes a wrong one harmless: it matches no
 *  row. The column grants leave `ask_enabled` and `model_scoring` out. */
export const updateProfile = (uid: string, patch: ProfilePatch) =>
  supabase.from('profiles').update(patch).eq('user_id', uid)

// ---------------------------------------------------------------------------
// U2.4: the person's own taste vector
// ---------------------------------------------------------------------------

/** One transaction in Postgres (`set_taste_vector`, `20261005180000`): the old
 *  active row goes inactive, the 2.0 row for `auth.uid()` is written active.
 *  No user id is passed because none is accepted — the function names only the
 *  caller. */
export const setTasteVector = (vector: VectorJson) =>
  supabase.rpc('set_taste_vector', { v: vector as unknown as Json })
